import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Clock } from "lucide-react";
import { api, trackEvent } from "@/lib/apiClient";
import Layout, { useSEO } from "@/components/Layout";
import { Button, Section, Overline, GoldRule } from "@/components/primitives";
import PhilosophyFlow from "@/components/PhilosophyFlow";
import { LeadForm } from "@/components/forms";
import NewsletterSection from "@/components/NewsletterSection";
import { FOUNDER_URL } from "@/lib/assets";
import { useSiteContent } from "@/lib/useSiteContent";

const TOPIC_META = {
  mindset: "How we interpret ourselves and the world.",
  discipline: "The quiet architecture of consistency.",
  resilience: "Recovering and adapting under real pressure.",
  leadership: "Influence built on clarity and character.",
  relationships: "Trust, communication and connection.",
  "intentional-living": "Choosing on purpose, not by default.",
};

export default function Home() {
  const [data, setData] = useState({ topics: [], featured: [], all: [], books: [], principles: [] });
  const site = useSiteContent();
  const hp = site?.homepage || {};
  const f = site?.founder || {};

  useSEO({
    title: "Impactful Mindset 101 — Better Thinking. Better Decisions. A More Intentional Life.",
    description: "A publishing and ideas platform for practical thinking, personal growth, leadership, discipline, resilience, relationships and intentional living.",
  });

  useEffect(() => {
    Promise.all([
      api.get("/topics"),
      api.get("/articles", { params: { featured: true } }),
      api.get("/articles"),
      api.get("/books", { params: { featured: true } }),
      api.get("/principles", { params: { featured: true } }),
    ]).then(([t, f, a, b, p]) =>
      setData({ topics: t.data, featured: f.data, all: a.data, books: b.data, principles: p.data })
    ).catch(() => {});
  }, []);

  const pool = data.featured.length ? data.featured : data.all;
  const lead = pool[0];
  const secondary = (pool.slice(1).concat(data.all).filter((x) => x && (!lead || x.id !== lead.id))).slice(0, 3);

  return (
    <Layout>
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 im-paper opacity-60" aria-hidden="true" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-20 pb-24 sm:pt-28 sm:pb-32">
          <div className="max-w-4xl">
            <div className="im-reveal flex items-center gap-3">
              <span className="gold-rule" />
              <Overline>{hp.hero_eyebrow || "Impactful Mindset 101 · Publishing & Ideas Platform"}</Overline>
            </div>
            <h1 className="im-reveal im-delay-1 font-serif-display font-bold tracking-[-0.02em] text-[#17181C] mt-7 leading-[0.98] text-[2.6rem] sm:text-6xl lg:text-7xl">
              {hp.hero_line1 || "Better Thinking."}<br />
              {hp.hero_line2 || "Better Decisions."}<br />
              <span className="text-[#0F2C59]">{hp.hero_line3 || "A More Intentional Life."}</span>
            </h1>
            <p className="im-reveal im-delay-2 text-lg sm:text-xl text-[#414855] mt-8 leading-relaxed max-w-2xl">
              {hp.hero_sub || "Practical, psychologically credible ideas about mindset, discipline, resilience, leadership, relationships and intentional living—without empty motivation or unrealistic positivity."}
            </p>
            <div className="im-reveal im-delay-3 flex flex-col sm:flex-row gap-4 mt-10">
              <Button to={hp.cta_primary_link || "/start-here"} data-testid="hero-start-here">{hp.cta_primary_text || "Start Here"}</Button>
              <Button to={hp.cta_secondary_link || "/ideas"} variant="secondary" data-testid="hero-explore-ideas">{hp.cta_secondary_text || "Explore Ideas"}</Button>
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHY / POSITIONING ============ */}
      <Section className="pb-6">
        <div className="border-t border-[#D8CBAE] pt-12 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <Overline className="overline-gold">Why Impactful Mindset</Overline>
          </div>
          <div className="md:col-span-8">
            <p className="font-serif-display text-2xl sm:text-3xl leading-snug text-[#17181C]">
              Meaningful growth is a process—not positive thinking. We publish ideas that help people see clearly,
              think well, and act with intention.
            </p>
          </div>
        </div>
      </Section>

      {/* ============ SIGNATURE PHILOSOPHY ============ */}
      <Section className="py-20">
        <div className="flex items-center gap-3">
          <span className="gold-rule" />
          <Overline>The Signature Framework</Overline>
        </div>
        <h2 className="font-serif-display text-3xl sm:text-5xl font-bold mt-4 text-[#17181C] tracking-[-0.01em]">Growth follows a sequence.</h2>
        <p className="text-[#414855] mt-5 max-w-2xl leading-relaxed">
          Lasting change rarely begins with willpower. It begins with awareness—and moves, step by step, toward the
          behavior that defines who we become.
        </p>
        <div className="mt-12"><PhilosophyFlow /></div>
      </Section>

      {/* ============ FEATURED IDEA ============ */}
      {lead && (
        <Section className="py-20 im-paper">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div className="flex items-center gap-3">
              <span className="gold-rule" />
              <Overline>Featured Idea</Overline>
            </div>
            <Link to="/ideas" className="text-sm font-semibold text-[#0F2C59] inline-flex items-center gap-2 im-link-underline">View all ideas <ArrowRight size={16} /></Link>
          </div>
          <div className="grid lg:grid-cols-12 gap-10">
            <Link to={`/ideas/article/${lead.slug}`} data-testid="featured-lead" className="lg:col-span-7 group">
              <div className="im-img-zoom aspect-[16/10] bg-[#E7E3DA] mb-6">
                <img src={lead.featured_image} alt={lead.title} className="w-full h-full object-cover" />
              </div>
              <Overline className="overline-gold">{lead.category}</Overline>
              <h3 className="font-serif-display text-2xl sm:text-4xl font-bold mt-3 leading-tight text-[#17181C] group-hover:text-[#0F2C59] transition-colors">{lead.title}</h3>
              <p className="text-[#414855] mt-4 leading-relaxed max-w-2xl">{lead.excerpt}</p>
              <span className="inline-flex items-center gap-2 mt-5 text-sm font-semibold text-[#0F2C59]">Read Article <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></span>
            </Link>
            <div className="lg:col-span-5 flex flex-col divide-y divide-[#D8CBAE] border-t border-[#D8CBAE]">
              {secondary.map((a) => (
                <Link key={a.id} to={`/ideas/article/${a.slug}`} data-testid={`featured-secondary-${a.slug}`} className="group py-6 first:pt-6">
                  <Overline className="overline-gold">{a.category}</Overline>
                  <h4 className="font-serif-display text-xl font-semibold mt-2 leading-snug text-[#17181C] group-hover:text-[#0F2C59] transition-colors">{a.title}</h4>
                  <div className="flex items-center gap-3 mt-2 text-xs text-[#8A8F99]">
                    <span className="inline-flex items-center gap-1"><Clock size={12} /> {a.reading_time} min</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* ============ EXPLORE IDEAS (numbered editorial) ============ */}
      <Section className="py-20">
        <div className="flex items-center gap-3">
          <span className="gold-rule" />
          <Overline>Explore Ideas</Overline>
        </div>
        <h2 className="font-serif-display text-3xl sm:text-5xl font-bold mt-4 text-[#17181C] tracking-[-0.01em]">Six territories of thought</h2>
        <div className="mt-12 border-t border-[#D8CBAE]">
          {data.topics.map((t, i) => (
            <Link
              key={t.slug}
              to={`/ideas/${t.slug}`}
              data-testid={`topic-card-${t.slug}`}
              className="group grid grid-cols-12 items-center gap-4 py-7 border-b border-[#E7E3DA] hover:bg-[#F3F0E9] transition-colors px-2 -mx-2"
            >
              <span className="im-numeral col-span-2 sm:col-span-1 text-2xl sm:text-3xl text-[#B58A3E]">{String(i + 1).padStart(2, "0")}</span>
              <div className="col-span-10 sm:col-span-4">
                <h3 className="font-serif-display text-xl sm:text-2xl font-semibold text-[#17181C] group-hover:text-[#0F2C59] transition-colors">{t.name}</h3>
              </div>
              <p className="hidden sm:block col-span-6 text-[#414855] text-sm leading-relaxed">{TOPIC_META[t.slug] || t.description}</p>
              <ArrowRight className="col-span-12 sm:col-span-1 justify-self-start sm:justify-self-end text-[#B58A3E] group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          ))}
        </div>
      </Section>

      {/* ============ PRINCIPLES ============ */}
      {data.principles.length > 0 && (
        <Section className="py-20 bg-[#0F2C59] text-white">
          <div className="flex items-center gap-3">
            <span className="gold-rule" />
            <Overline className="!text-[#C9A25A]">Impactful Mindset Principles</Overline>
          </div>
          <h2 className="font-serif-display text-3xl sm:text-5xl font-bold mt-4 tracking-[-0.01em]">Ideas we return to</h2>
          <div className="grid md:grid-cols-2 gap-x-14 gap-y-12 mt-12">
            {data.principles.slice(0, 4).map((p) => (
              <div key={p.id} data-testid={`principle-${p.slug || p.id}`} className="relative">
                <span className="font-serif-display text-6xl text-[#C9A25A] leading-none absolute -top-4 -left-1 opacity-70" aria-hidden="true">“</span>
                <blockquote className="font-serif-display text-xl sm:text-2xl leading-snug text-white pl-8">{p.statement}</blockquote>
                <div className="pl-8 mt-4 flex items-center gap-3">
                  <span className="h-px w-8 bg-[#C9A25A]" />
                  <span className="text-xs tracking-[0.2em] uppercase text-slate-300">{p.category}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 pl-0 md:pl-8">
            <Button to="/principles" variant="outlineWhite" data-testid="principles-cta">View the Principles Library</Button>
          </div>
        </Section>
      )}

      {/* ============ BOOKS & PUBLICATIONS ============ */}
      {data.books.length > 0 && (
        <Section className="py-20">
          <div className="flex items-center gap-3">
            <span className="gold-rule" />
            <Overline>Books &amp; Publications</Overline>
          </div>
          <h2 className="font-serif-display text-3xl sm:text-5xl font-bold mt-4 text-[#17181C] tracking-[-0.01em]">Serious ideas, in print</h2>
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            {data.books.map((book) => (
              <div key={book.id} data-testid={`home-book-${book.slug}`} className="im-paper border border-[#E7E3DA] p-8 sm:p-10 flex flex-col sm:flex-row gap-8 items-center">
                <Link to={`/books/${book.slug}`} className="shrink-0">
                  <img src={book.cover_image} alt={book.title} loading="lazy" className="w-40 sm:w-44 drop-shadow-[0_20px_35px_rgba(10,29,61,0.28)]" />
                </Link>
                <div className="text-center sm:text-left">
                  {book.status === "coming_soon" && <span className="inline-block text-[0.65rem] tracking-[0.2em] font-bold text-[#B58A3E] border border-[#D8CBAE] px-2 py-1 mb-3">FORTHCOMING</span>}
                  <h3 className="font-serif-display text-xl sm:text-2xl font-bold text-[#17181C] leading-tight">{book.title}</h3>
                  <p className="text-sm text-[#8A8F99] mt-2 italic font-serif-display">{book.subtitle}</p>
                  <p className="text-[#414855] text-sm mt-4 leading-relaxed">{book.short_description}</p>
                  <div className="flex flex-wrap gap-3 mt-6 justify-center sm:justify-start">
                    <Button to={`/books/${book.slug}`} variant="secondary" className="text-xs">Learn More</Button>
                    {book.status === "coming_soon"
                      ? <Button to="/join" variant="gold" className="text-xs">Get Notified</Button>
                      : <Button to={`/books/${book.slug}`} variant="gold" className="text-xs">Buy the Book</Button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ============ FREE RESOURCE ============ */}
      <Section className="py-20 im-paper" id="free-resource">
        <div className="grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="flex items-center gap-3">
              <span className="gold-rule" />
              <Overline>Free Resource</Overline>
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-bold mt-4 text-[#17181C] tracking-[-0.01em]">The 7-Day Inner Conversation Reset</h2>
            <p className="text-[#414855] mt-5 leading-relaxed">
              A short, practical guide to noticing and reshaping the way you talk to yourself—one week, one honest
              observation at a time. Grounded in practice, not empty positivity.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-[#414855]">
              <li>· What your inner voice is—and why it matters</li>
              <li>· Who this is for: anyone ready to think more honestly</li>
              <li>· What you'll do: seven small, repeatable practices</li>
            </ul>
          </div>
          <div className="bg-white border border-[#E7E3DA] p-8 shadow-[0_20px_50px_rgba(10,29,61,0.06)]">
            <h3 className="font-serif-display text-xl font-semibold text-[#17181C] mb-1">Get the Free 7-Day Reset</h3>
            <p className="text-xs text-[#8A8F99] mb-5">First name and email—that's all.</p>
            <LeadForm resourceSlug="the-7-day-inner-conversation-reset" campaign="homepage_lead_magnet" testidPrefix="home-lead" />
          </div>
        </div>
      </Section>

      {/* ============ LEARN · APPLY · GROW ============ */}
      <Section className="py-20">
        <div className="flex items-center gap-3">
          <span className="gold-rule" />
          <Overline>Learn · Apply · Grow</Overline>
        </div>
        <h2 className="font-serif-display text-3xl sm:text-5xl font-bold mt-4 text-[#17181C] tracking-[-0.01em]">From ideas to practice</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {[
            ["Learn", "Courses and guided programs that build knowledge over time.", "/programs"],
            ["Apply", "Assessments, workbooks and tools to put ideas to work.", "/resources"],
            ["Grow", "Workshops, seminars and speaking—live and immersive.", "/programs"],
          ].map(([title, desc, to], i) => (
            <Link key={title} to={to} className="group border border-[#E7E3DA] bg-white p-8 hover:border-[#B58A3E] transition-colors">
              <span className="im-numeral text-3xl text-[#B58A3E]">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-serif-display text-xl font-semibold mt-4 text-[#17181C]">{title}</h3>
              <p className="text-[#414855] text-sm mt-2 leading-relaxed">{desc}</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F2C59] mt-5 im-link-underline">Explore <ArrowRight size={14} /></span>
            </Link>
          ))}
        </div>
      </Section>

      {/* ============ FOUNDER ============ */}
      <Section className="py-20 im-paper">
        <div className="grid md:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="md:col-span-5">
            <div className="im-img-zoom aspect-[4/5] bg-[#E7E3DA] overflow-hidden">
              <img src={f.photo_url || FOUNDER_URL} alt="Lance Cervantes, founder of Impactful Mindset 101" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="flex items-center gap-3">
              <span className="gold-rule" />
              <Overline>From the Founder</Overline>
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-bold mt-4 text-[#17181C]">{f.name || "Lance Cervantes"}</h2>
            <p className="text-sm tracking-[0.15em] uppercase text-[#B58A3E] mt-2 font-semibold">{f.descriptor || "Author · Educator · Entrepreneur"}</p>
            <p className="text-[#414855] mt-6 leading-relaxed max-w-xl">
              {f.short_bio || "Lance Cervantes is the founder, author and educator behind Impactful Mindset 101. His work focuses on practical ideas about mindset, leadership, discipline, personal growth and intentional living—grounded in reflection, responsibility and action rather than hype or shortcuts."}
            </p>
            <p className="font-serif-display italic text-xl text-[#0F2C59] mt-6">{f.quote || "— Lance Cervantes"}</p>
            <Button to="/about" variant="secondary" className="mt-8" data-testid="founder-about-link">Read Lance's Story</Button>
          </div>
        </div>
      </Section>

      <NewsletterSection />
    </Layout>
  );
}
